import { useEffect, useState } from 'react';
import { Modal } from '@/components/common/AppModal';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import { getIndexerErrorInfo } from '../api/errors';
import {
  useDeleteProjectGoogleCredentialsMutation,
  useLazyGetProjectGoogleCredentialsQuery,
} from '../api/indexerApi';
import { OnboardingCredentialsUpload } from './onboarding/OnboardingGoogleSetupStep';
import { useProject } from '../lib/project-context';
import { copyText } from '../lib/utils';
import { formatTime } from '../lib/datetime';
import { SettingsPagePreload } from './preloads/IndexerPreloads';

export function GoogleIndexerPrerequisitePanel() {
  const { project, projectRef } = useProject();
  const [email, setEmail] = useState<string | null>(null);
  const [uploadedAt, setUploadedAt] = useState<string | null>(null);
  const [configured, setConfigured] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [getProjectGoogleCredentials] = useLazyGetProjectGoogleCredentialsQuery();
  const [deleteProjectGoogleCredentials] =
    useDeleteProjectGoogleCredentialsMutation();

  async function reload() {
    if (!project || !projectRef) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getProjectGoogleCredentials(projectRef, true).unwrap();
      setEmail(data.clientEmail);
      setUploadedAt(data.uploadedAt);
      setConfigured(data.configured);
    } catch {
      setConfigured(false);
      setEmail(null);
      setUploadedAt(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!project || !projectRef) {
      setLoading(false);
      return;
    }
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when project changes
  }, [project?.id, projectRef]);

  async function onCopy() {
    if (!email) return;
    const ok = await copyText(email);
    setCopied(ok);
    if (ok) setTimeout(() => setCopied(false), 2000);
  }

  async function onConfirmDelete() {
    if (!project || !projectRef || deleteBusy) return;
    setDeleteBusy(true);
    setError(null);
    try {
      await deleteProjectGoogleCredentials(projectRef).unwrap();
      setConfigured(false);
      setEmail(null);
      setUploadedAt(null);
      setDeleteOpen(false);
    } catch (err) {
      setError(getIndexerErrorInfo(err).message || 'حذف کلید ناموفق بود');
    } finally {
      setDeleteBusy(false);
    }
  }

  if (!project || !projectRef) return null;

  if (loading) {
    return <SettingsPagePreload cards={1} />;
  }

  return (
    <>
      <Card className="mb-0">
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <span>تنظیمات گوگل (Indexing / Search Console)</span>
          <span
            className={`badge ${configured ? 'text-bg-primary' : 'text-bg-warning'}`}
          >
            {configured ? 'تنظیم شده' : 'تنظیم نشده'}
          </span>
        </Card.Header>
        <Card.Body>
          <p className="text-secondary small mb-3">
            هر پروژه کلید JSON سرویس‌اکانت خودش را اینجا آپلود می‌کند. همان ایمیل
            باید در Search Console با نقش <strong>Owner</strong> اضافه شده باشد.
          </p>

          {error && (
            <Alert variant="danger" className="py-2 small">
              {error}
            </Alert>
          )}

          {!configured && (
            <div className="d-grid gap-3">
              <Alert variant="warning" className="mb-0 py-2 small">
                فایل JSON سرویس‌اکانت برای این پروژه آپلود نشده است؛ تا آپلود مجدد،
                درخواست ایندکس و بازرسی وضعیت کار نمی‌کند.
              </Alert>
              <OnboardingCredentialsUpload
                projectId={projectRef}
                clientEmail={email}
                onUploaded={(nextEmail) => {
                  setEmail(nextEmail);
                  setConfigured(true);
                  setUploadedAt(new Date().toISOString());
                  void reload();
                }}
              />
            </div>
          )}

          {configured && (
            <div className="d-grid gap-3">
              <Alert variant="success" className="mb-0 py-2 small">
                JSON سرویس‌اکانت آپلود شده و برای این پروژه فعال است.
                {uploadedAt && (
                  <>
                    {' '}
                    زمان آپلود: {formatTime(uploadedAt)}
                  </>
                )}
              </Alert>
              {email && (
                <div>
                  <div className="text-secondary small mb-1">
                    ایمیل سرویس‌اکانت (برای Search Console)
                  </div>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <code className="border rounded px-3 py-2 bg-body-tertiary mono flex-grow-1">
                      {email}
                    </code>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => void onCopy()}
                    >
                      {copied ? 'کپی شد' : 'کپی'}
                    </Button>
                  </div>
                </div>
              )}
              <div className="d-flex justify-content-end w-100 mt-3 pt-1">
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => setDeleteOpen(true)}
                >
                  حذف JSON سرویس‌اکانت
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={deleteOpen}
        onHide={() => {
          if (!deleteBusy) setDeleteOpen(false);
        }}
        centered
      >
        <Modal.Header closeButton={!deleteBusy}>
          <Modal.Title className="fs--1 fw-semibold mb-0">حذف کلید گوگل</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger" className="small mb-0">
            با حذف JSON، دسترسی این پروژه به Indexing API و Search Console قطع
            می‌شود و تا آپلود دوبارهٔ کلید در همین صفحه، امکان استفاده از ابزار
            ایندکسر نیست.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            disabled={deleteBusy}
            onClick={() => setDeleteOpen(false)}
          >
            انصراف
          </Button>
          <Button
            variant="danger"
            disabled={deleteBusy}
            onClick={() => void onConfirmDelete()}
          >
            {deleteBusy && (
              <Spinner
                size="sm"
                animation="border"
                className="me-2"
                style={{ width: 12, height: 12 }}
              />
            )}
            حذف کلید
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
